import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import Navbar from '../components/Navbar';
import { Seo } from '../components/Seo';
import backdropGif from '../assets/gamesbackdrop.gif';
import { API_BASE_URL } from '../utils/apiClient';
import './GameContributionHub.css';

const statusLabels = {
  pending: '待审核',
  approved: '已通过',
  rejected: '未通过'
};

const GameContributionHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.pathname.endsWith('/requests')
    ? 'requests'
    : location.pathname.endsWith('/recent') ? 'recent' : 'home';
  const [requests, setRequests] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backdropLoaded, setBackdropLoaded] = useState(false);

  const resolveImage = (source) => {
    if (!source) return '';
    if (/^(https?:\/\/|data:|blob:)/i.test(source)) return source;
    if (source.startsWith('/igdb-img/')) return source;
    return `${API_BASE_URL}${source.startsWith('/') ? source : `/${source}`}`;
  };

  const hasImage = (source) => Boolean(source && !String(source).includes('未找到'));

  const parseScreenshots = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(hasImage);
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(hasImage) : [];
    } catch (_error) {
      return String(value).split(',').map((item) => item.trim()).filter(hasImage);
    }
  };

  useEffect(() => {
    if (mode === 'requests') {
      setLoading(true);
      apiClient.get('/game-applications/pending-for-contributors')
        .then((response) => setRequests(response.data))
        .catch((requestError) => setError(requestError.message || '暂时无法读取游戏申请。'))
        .finally(() => setLoading(false));
    }
    if (mode === 'recent') {
      setLoading(true);
      apiClient.get('/visitor-game-submissions/recent')
        .then((response) => setSubmissions(response.data))
        .catch((statusError) => setError(statusError.message || '暂时无法读取提交记录。'))
        .finally(() => setLoading(false));
    }
  }, [mode]);

  const heading = useMemo(() => {
    if (mode === 'requests') return '处理游戏申请';
    if (mode === 'recent') return '近期游戏提交';
    return '为 RGM 贡献游戏';
  }, [mode]);

  return (
    <div className="contribution-page">
      <Seo title={`${heading} | 怀旧游戏博物馆`} description="帮助怀旧游戏博物馆扩充和整理游戏馆藏。" robots="noindex, nofollow" />
      <div className={`contribution-backdrop ${backdropLoaded ? 'loaded' : ''}`} aria-hidden="true">
        <img src={backdropGif} alt="" onLoad={() => setBackdropLoaded(true)} />
        <div />
      </div>
      <Navbar />
      <main className="contribution-shell">
        {mode !== 'home' && <button type="button" className="contribution-back" onClick={() => navigate('/contribute-games')}>← 返回贡献入口</button>}
        <header className="contribution-header">
          <span className="contribution-eyebrow">怀旧游戏博物馆 · 社区共建</span>
          <h1>{heading}</h1>
          <p>感谢您帮助支持和壮大 RGM！您的昵称会在您提交的游戏上显示。</p>
        </header>

        {mode === 'home' && (
          <div className="contribution-choice-grid">
            <Link className="contribution-choice-card primary" to="/submit-game">
              <div><h2>添加你的游戏收藏</h2><p>帮助壮大我们的游戏库和档案</p></div>
              <span className="contribution-choice-arrow">→</span>
            </Link>
            <Link className="contribution-choice-card" to="/contribute-games/requests">
              <div><h2>处理游戏申请</h2><p>协助我们的馆员处理用户提交的游戏申请</p></div>
              <span className="contribution-choice-arrow">→</span>
            </Link>
            <Link className="contribution-choice-card compact" to="/contribute-games/recent">
              <div><h2>查看近期提交</h2><p>查看最近提交的游戏及其审核状态</p></div>
              <span className="contribution-choice-arrow">→</span>
            </Link>
          </div>
        )}

        {error && <div className="contribution-error">{error}</div>}
        {loading && <div className="contribution-empty">正在载入…</div>}

        {mode === 'requests' && !loading && (
          requests.length ? (
            <div className="request-contribution-list">
              {requests.map((request) => (
                <article className="request-contribution-card" key={request.id}>
                  <div className="request-contribution-poster">
                    {hasImage(request.poster) ? <img src={resolveImage(request.poster)} alt={`${request.title_cn || request.title_en} 封面`} loading="lazy" /> : <span>暂无封面</span>}
                  </div>
                  <div className="request-contribution-copy">
                    <span className="request-number">游戏申请 #{request.id}</span>
                    <h2>{request.title_cn || request.title_en}</h2>
                    {request.title_cn && request.title_en && <p className="request-english-title">{request.title_en}</p>}
                    <div className="request-meta"><span><b>平台</b>{request.platform || '未知'}</span><span><b>年份</b>{request.release_date ? String(request.release_date).slice(0, 4) : '未知'}</span></div>
                    <div className="request-expanded">
                      {request.description && <p>{request.description}</p>}
                      {parseScreenshots(request.screenshots).length > 0 && <div className="request-screenshots">{parseScreenshots(request.screenshots).map((src, index) => <img key={`${src}-${index}`} src={resolveImage(src)} alt={`申请截图 ${index + 1}`} loading="lazy" />)}</div>}
                    </div>
                  </div>
                  <Link className="request-create-button" to={`/submit-game?fromApplication=${encodeURIComponent(request.id)}`}>一键创建</Link>
                </article>
              ))}
            </div>
          ) : <div className="contribution-empty">目前没有尚未处理的游戏申请，感谢您的关注！</div>
        )}

        {mode === 'recent' && !loading && (
          submissions.length ? (
            <div className="recent-submission-list">
              {submissions.map((submission) => (
                <article className="recent-submission-card" key={submission.id}>
                  <div className="recent-submission-cover">
                    {submission.cover_url ? <img src={resolveImage(submission.cover_url)} alt="" loading="lazy" /> : <span>暂无封面</span>}
                  </div>
                  <div>
                    <span className="recent-submission-number">提交 #{submission.id}</span>
                    <h2>{submission.titlechn || submission.titleeng || '未命名游戏'}</h2>
                    <p>贡献者：{submission.contributor_alias || '未署名'}</p>
                    <p>{new Date(submission.submitted_at).toLocaleString('zh-CN')}</p>
                    {submission.game_application_id && <p>关联游戏申请 #{submission.game_application_id}</p>}
                  </div>
                  <div className={`recent-status ${submission.status}`}>{statusLabels[submission.status] || submission.status}</div>
                  {submission.review_comment && <div className="review-comment"><strong>馆员留言</strong><p>{submission.review_comment}</p></div>}
                  {submission.status === 'approved' && submission.published_game_id && <Link className="published-game-link" to={`/game/${submission.published_game_id}`}>立即游玩 →</Link>}
                </article>
              ))}
            </div>
          ) : <div className="contribution-empty">目前还没有游戏提交记录。</div>
        )}
      </main>
    </div>
  );
};

export default GameContributionHub;
